import { useEffect, useRef, useState } from "react";
import { loadYouTubePlayerApi } from "../lib/youtubePlayerApi";

const PLAYER_STATES = { "-1": "ready", 0: "ended", 1: "playing", 2: "paused", 3: "buffering", 5: "ready" };

export const useYouTubePlayer = ({ src, title }) => {
  const hostRef = useRef(null);
  const [state, setState] = useState("loading");
  const [errorCode, setErrorCode] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const host = hostRef.current;
    let active = true;
    let started = false;
    let terminal = false;
    let player;
    let timer;
    setState("loading");
    setErrorCode(null);
    const fail = (code) => {
      if (!active || terminal) return;
      terminal = true;
      window.clearTimeout(timer);
      setErrorCode(String(code));
      setState("error");
    };

    const start = () => {
      if (started || !active) return;
      started = true;
      // The provider owns this iframe; React owns only the host container.
      const iframe = document.createElement("iframe");
      iframe.src = src;
      iframe.title = title;
      iframe.loading = "lazy";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      iframe.allow = "encrypted-media; picture-in-picture; fullscreen";
      iframe.allowFullscreen = true;
      iframe.className = "absolute inset-0 block h-full w-full border-0";
      iframe.dataset.testid = "tech-video-player";
      iframe.onerror = () => fail("network");
      host.appendChild(iframe);
      timer = window.setTimeout(() => fail("timeout"), 15000);

      loadYouTubePlayerApi().then((YT) => {
        if (!active || terminal) return;
        player = new YT.Player(iframe, {
          events: {
            onReady: () => {
              if (!active || terminal) return;
              window.clearTimeout(timer);
              setState("ready");
            },
            onStateChange: ({ data }) => {
              if (active && !terminal) setState(PLAYER_STATES[data] || "ready");
            },
            onError: ({ data }) => fail(data),
          },
        });
      }).catch(() => fail("api"));
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); start(); }
    });
    observer.observe(host);

    return () => {
      active = false;
      observer.disconnect();
      window.clearTimeout(timer);
      player?.destroy();
      host.replaceChildren();
    };
  }, [src, title, attempt]);

  return { hostRef, state, errorCode, retry: () => setAttempt((value) => value + 1) };
};